/// <reference path="../../types.d.ts" />

import { createReducer } from '@reduxjs/toolkit';
import { Action } from 'typescript-fsa';
import InitialState from '../../reducers/initial-state';
import { LOAD_STATES } from '../../reducers/common';
import * as UIActions from '../../actions/ui';
import { jumpContext } from '../../actions/contexts.action';
import {
  DatasetFreshnessResponse,
  FilterOptionsResponse
} from './api';
import {
  fetchSummary,
  loadFilterOptions,
  loadFreshness,
  setHealthSystem,
  setServiceLine,
  setTenant,
  setDateSelection
} from './actions';

const reducer = createReducer(InitialState.automationDashboard, builder => {
  // Default cases from cmxReducerFactory: reset state on logout or context jump
  builder.addCase(UIActions.logout.type, () => ({
    ...InitialState.automationDashboard
  }));
  builder.addCase(jumpContext.pending.type, () => ({
    ...InitialState.automationDashboard
  }));

  builder.addCase(
    setHealthSystem.type,
    (state, action: Action<string>) => ({
      ...state,
      // A site belongs to one health system, so a new health system starts unfiltered by site.
      filters: { ...state.filters, health_system_code: action.payload, tenant_code: '' }
    })
  );

  builder.addCase(setTenant.type, (state, action: Action<string>) => ({
    ...state,
    filters: { ...state.filters, tenant_code: action.payload }
  }));

  builder.addCase(setServiceLine.type, (state, action: Action<string>) => ({
    ...state,
    filters: { ...state.filters, service_line: action.payload }
  }));

  builder.addCase(
    setDateSelection.type,
    (
      state,
      action: Action<{ presetId: string; dos_start: string; dos_end: string }>
    ) => ({
      ...state,
      dateRangePresetId: action.payload.presetId,
      dateRange: {
        dos_start: action.payload.dos_start,
        dos_end: action.payload.dos_end
      }
    })
  );

  builder.addCase(fetchSummary.async.started.type, state => ({
    ...state,
    loadState: LOAD_STATES.started,
    error: null
  }));

  builder.addCase(
    fetchSummary.async.done.type,
    (
      state,
      action: Action<{ result: CMx.AutomationDashboardSummary }>
    ) => ({
      ...state,
      loadState: LOAD_STATES.done,
      data: action.payload.result,
      error: null
    })
  );

  builder.addCase(
    fetchSummary.async.failed.type,
    (state, action: Action<{ error?: Error }>) => ({
      ...state,
      loadState: LOAD_STATES.failed,
      error:
        action.payload.error?.message ??
        'Failed to load automation dashboard'
    })
  );

  builder.addCase(
    loadFilterOptions.async.done.type,
    (state, action: Action<{ result: FilterOptionsResponse }>) => ({
      ...state,
      filterOptions: {
        healthSystems: action.payload.result.healthSystems,
        serviceLines: action.payload.result.serviceLines,
        subServiceLines: action.payload.result.subServiceLines,
        categories: action.payload.result.categories,
        tenants: action.payload.result.tenants ?? []
      }
    })
  );

  builder.addCase(
    loadFreshness.async.done.type,
    (state, action: Action<{ result: DatasetFreshnessResponse }>) => ({
      ...state,
      freshness: {
        dataAsOf: action.payload.result.dataAsOf,
        stale: action.payload.result.stale
      }
    })
  );
});

export default reducer;
